import type { ComponentProps, ElementType } from 'react'
import { motion, type MotionProps } from 'motion/react'

const variants = {
  fadeUp: {
    hidden: { opacity: 0, y: 32 },
    visible: { opacity: 1, y: 0 }
  },
  fadeUpSoft: {
    hidden: { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0 }
  },
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  }
}

type MotionTag = typeof motion.div

type RevealProps<T extends ElementType | MotionTag> = {
  as?: T
  variant?: keyof typeof variants
  delay?: number
  viewportAmount?: number
  viewportMargin?: NonNullable<MotionProps['viewport']>['margin']
} & Omit<ComponentProps<T extends MotionTag ? T : typeof motion.div>, 'as'>

export function Reveal<T extends ElementType | MotionTag = MotionTag>({
  as,
  variant = 'fadeUp',
  delay = 0,
  viewportAmount = 0.4,
  viewportMargin,
  transition,
  ...props
}: RevealProps<T>) {
  const componentFromString =
    typeof as === 'string'
      ? (motion[as as keyof typeof motion] as MotionTag | undefined)
      : undefined

  const Component = (componentFromString ??
    (typeof as === 'string' ? motion.div : (as ?? motion.div))) as MotionTag

  return (
    <Component
      initial="hidden"
      whileInView="visible"
      viewport={{
        once: true,
        amount: viewportAmount,
        margin: viewportMargin
      }}
      variants={variants[variant]}
      transition={{
        duration: 0.6,
        ease: 'easeOut',
        delay,
        ...transition
      }}
      {...(props as MotionProps)}
    />
  )
}
